import {useEffect,useRef,useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Capacitor} from '@capacitor/core';
import {Contacts} from '@capacitor/contacts';
import {ContactRound} from 'lucide-react';
import {useIsPhone} from '../lib/useIsPhone.js';
import {contactFailure,phoneValue,type WebContact} from '../lib/deviceContact.js';

interface WebContactManager {select:(fields:string[],options:{multiple:boolean})=>Promise<WebContact[]>}

/** Opens the device picker only. Only phone strings survive the selected contact. */
export function DevicePhonePicker({onPick}:{onPick:(phone:string)=>void}) {
  const phone=useIsPhone();
  const native=Capacitor.isNativePlatform()&&Capacitor.isPluginAvailable('Contacts');
  const web=typeof navigator==='undefined'?undefined:(navigator as Navigator&{contacts?:WebContactManager}).contacts;
  if(!native&&!(phone&&web&&window.isSecureContext))return null;
  return <AvailablePhonePicker native={native} web={web} onPick={onPick}/>;
}

function AvailablePhonePicker({native,web,onPick}:{native:boolean;web:WebContactManager|undefined;onPick:(phone:string)=>void}) {
  const {t}=useTranslation();
  const [busy,setBusy]=useState(false),[numbers,setNumbers]=useState<string[]>([]),[error,setError]=useState<string|null>(null);
  const pending=useRef(false),mounted=useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const pick=async()=>{
    if(pending.current)return;
    pending.current=true;setBusy(true);setError(null);setNumbers([]);
    try{
      // Android's plugin requests READ_CONTACTS internally. No address-book query is used.
      const values=native?(await Contacts.pickContact()).phoneNumbers?.map(item=>item.value??''):(await web!.select(['tel'],{multiple:false}))[0]?.tel;
      if(!mounted.current)return;
      const phones=[...new Set((values??[]).map(phoneValue).filter(Boolean))];
      if(phones.length===1)onPick(phones[0]!);
      else if(phones.length>1)setNumbers(phones);
      else setError('common.contactUnavailable');
    }catch(cause){
      const failure=contactFailure(cause);
      if(mounted.current&&failure!=='cancelled')setError(failure==='denied'?'common.contactPermissionDenied':'common.contactUnavailable');
    }finally{pending.current=false;if(mounted.current)setBusy(false);}
  };
  return <>
    <button type="button" aria-label={t('common.pickContact')} title={t('common.pickContact')} disabled={busy} aria-busy={busy} className="absolute end-0 top-0 min-h-10 min-w-11 grid place-items-center rounded-lg text-primary hover:bg-muted disabled:opacity-50" onClick={()=>void pick()}><ContactRound className="h-4 w-4" aria-hidden/></button>
    {numbers.length>0&&<span className="absolute end-0 top-11 z-50 block w-64 max-w-full rounded-lg border border-border bg-popover p-1 shadow-lg" role="group" aria-label={t('common.pickContact')}>
      {numbers.map(number=><button key={number} type="button" dir="ltr" className="block min-h-11 w-full rounded px-3 text-start text-sm hover:bg-muted" onClick={()=>{setNumbers([]);onPick(number);}}>{number}</button>)}
      <button type="button" className="min-h-11 w-full text-sm text-muted-foreground" onClick={()=>setNumbers([])}>{t('common.cancel')}</button>
    </span>}
    {error&&<span role="alert" className="mt-1 block text-xs text-warning">{t(error)}</span>}
  </>;
}
